<template>
  <div class="relative m-6">
    <!-- Header section -->
    <div class="sm:flex sm:items-center mb-8">
      <div class="flex flex-auto items-center justify-between">
        <div class="flex items-center space-x-5">
          <div class="shrink-0">
            <div class="relative rounded-xl bg-orange-600/70 p-1">
              <mdicon name="tune-variant" size="62" class="text-orange-100" aria-hidden="true"></mdicon>
            </div>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Advanced settings</h1>
            <p class="text-sm font-medium text-gray-500">Manage advanced settings in your account</p>
          </div>
        </div>
        <!-- Save Button -->
        <button
          :disabled="!hasChanges"
          @click="saveChanges"
          severity="primary"
          autofocus
          :class="[
            'mr-2 inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
            hasChanges ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-indigo-400 cursor-not-allowed',
          ]"
        >
          Save
        </button>
      </div>
    </div>

    <!-- Content section -->
    <div class="space-y-6">
      <div v-for="(group, index) in advanced_settings" :key="index" class="rounded-xl border border-gray-200 overflow-hidden">
        <Disclosure v-slot="{ open }" as="div" :default-open="false">
          <!-- Disclosure header -->
          <DisclosureButton class="flex w-full items-center justify-between bg-gray-50 px-6 py-4 text-left hover:bg-gray-100 transition-colors">
            <span class="text-lg font-semibold text-gray-900">{{ group.name }}</span>
            <ChevronUpIcon :class="[open ? 'rotate-180' : '', 'size-5 text-gray-500 transition-transform']" />
          </DisclosureButton>

          <!-- Disclosure content -->
          <DisclosurePanel class="p-6">
            <div class="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-5">
              <!-- Left column - Group description -->
              <div class="md:pr-4">
                <h2 class="text-base font-semibold text-gray-900">{{ group.name }}</h2>
                <p class="mt-2 text-sm text-gray-600">{{ group.description }}</p>
              </div>

              <!-- Right column - Settings -->
              <div v-if="'items' in group" class="md:col-span-4">
                <div class="bg-white rounded-xl shadow-sm ring-1 ring-gray-900/5 divide-y divide-gray-200">
                  <div v-for="(item, itemIndex) in group.items" :key="itemIndex" class="p-6">
                    <ToggleGroup :model-value="getSettingValue(item.field)" :name="item.name" :description="item.description" @update:model-value="updateLocalSetting(item.field, $event)" />
                  </div>
                </div>
              </div>
              <div v-else-if="'component' in group" class="md:col-span-4">
                <component :is="group.component"></component>
              </div>
            </div>
          </DisclosurePanel>
        </Disclosure>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, onMounted, watch } from 'vue';
  import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
  import { ChevronUpIcon } from '@heroicons/vue/20/solid';
  import { useSettingsStore } from '@/store/SettingsStore';
  import { doc, updateDoc } from 'firebase/firestore';
  import { fbDb } from '@/firebaseApp';
  import { useAuthStore } from '@/store/AuthStore';
  import ToggleGroup from '@/components/Toggles/ToggleGroup.vue';
  import JobsMapping from '@/components/Settings/JobsMapping.vue';

  const settingsStore = useSettingsStore();
  const authStore = useAuthStore();

  // Local settings state
  const localSettings = ref({});
  const originalSettings = ref({});
  const hasChanges = computed(() => {
    const localStr = JSON.stringify(localSettings.value);
    const originalStr = JSON.stringify(originalSettings.value);
    const result = localStr !== originalStr;
    console.log('hasChanges:', result, { local: localStr, original: originalStr });
    return result;
  });

  // Initialize local settings when component mounts
  onMounted(() => {
    initializeLocalSettings();
  });

  // Watch for changes in tenant settings
  watch(
    () => settingsStore.tenantSettings,
    (newSettings) => {
      if (newSettings) {
        initializeLocalSettings();
      }
    },
  );

  const advanced_settings = computed(() => {
    const settingsList = [
      {
        name: 'Audit',
        description: 'Define audit settings for the system',
        items: [
          {
            name: 'KB Audit Logs',
            description: 'View and manage audit logs for knowledge base',
            icon: 'history',
            field: 'featureToggles.kbAuditEnabled',
            type: 'boolean',
          },
        ],
      },
      {
        name: 'Ingest and QA Analytics Jobs Mapping',
        description: 'Define which jobs are used for Ingestion and QA analytics',
        component: JobsMapping,
      },
      /*
    {
      name: 'Assistants',
      description: 'Define additional assistant features for the system',
      items: [
        {
          name: 'Data Assistant Enabled',
          description: 'Enable or disable the data assistant',
          icon: 'history',
          field: 'featureToggles.dataAssistantEnabled',
          type: 'boolean',
        },


      ],
    },

     */
    ];
    return settingsList;
  });

  // Initialize local settings from tenant settings
  const initializeLocalSettings = () => {
    const settings = {};
    advanced_settings.value.forEach((group) => {
      if (Array.isArray(group.items)) {
        group.items.forEach((item) => {
          if (item.field.includes('.')) {
            const [topLevel, nestedField] = item.field.split('.');
            if (!settings[topLevel]) {
              settings[topLevel] = {};
            }
            settings[topLevel][nestedField] = getNestedValue(settingsStore.tenantSettings, item.field) ?? false;
          } else {
            settings[item.field] = getNestedValue(settingsStore.tenantSettings, item.field) ?? false;
          }
        });
      }
    });
    localSettings.value = { ...settings };
    originalSettings.value = JSON.parse(JSON.stringify(settings));
  };

  // Update local settings
  const updateLocalSetting = (field, value) => {
    if (field.includes('.')) {
      const [topLevel, nestedField] = field.split('.');
      localSettings.value = {
        ...localSettings.value,
        [topLevel]: {
          ...localSettings.value[topLevel],
          [nestedField]: value,
        },
      };
    } else {
      localSettings.value = {
        ...localSettings.value,
        [field]: value,
      };
    }
  };

  // Save changes to Firebase
  const saveChanges = async () => {
    const tenantSettingsDoc = doc(fbDb, 'tenants', authStore.tenantId);

    try {
      const updateData = {};

      // Prepare update data
      Object.entries(localSettings.value).forEach(([field, value]) => {
        if (typeof value === 'object') {
          // Handle nested objects (like featureToggles)
          updateData[field] = {
            ...(settingsStore.tenantSettings[field] || {}),
            ...value,
          };
        } else {
          updateData[field] = value;
        }
      });

      await updateDoc(tenantSettingsDoc, updateData);
      originalSettings.value = JSON.parse(JSON.stringify(localSettings.value));
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  // Get setting value from local settings
  const getSettingValue = (field) => {
    if (field.includes('.')) {
      const [topLevel, nestedField] = field.split('.');
      return localSettings.value[topLevel]?.[nestedField] ?? false;
    }
    return localSettings.value[field] ?? false;
  };

  // Function to get nested object value using string path
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };
</script>
